import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { SEO_CATEGORIES, hubPath } from '../data/regions';

interface Category {
  id: string;
  value: string;
  icon: string;
}

interface Props {
  categories: Category[];
  getIcon: (icon: string, props?: any) => React.ReactNode;
  /** Object count per category `value` (e.g. `{ APARTMENT: 17 }`) — omit or
   *  leave a category out while its count is still loading/unknown. */
  counts?: Record<string, number>;
}

function pluralizeRu(n: number, forms: [string, string, string]): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return forms[0];
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return forms[1];
  return forms[2];
}

const CATEGORY_IMAGES: Record<string, string> = {
  apartments:
    'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=640&q=70&auto=format',
  houses:
    'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=640&q=70&auto=format',
  plots:
    'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=640&q=70&auto=format',
  commercial:
    'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=640&q=70&auto=format',
};

// Accent colors per category
const CATEGORY_COLORS: Record<
  string,
  { from: string; to: string; text: string; bg: string }
> = {
  apartments: {
    from: 'from-blue-600',
    to: 'to-blue-400',
    text: 'text-blue-600',
    bg: 'bg-blue-50',
  },
  houses: {
    from: 'from-emerald-600',
    to: 'to-emerald-400',
    text: 'text-emerald-600',
    bg: 'bg-emerald-50',
  },
  plots: {
    from: 'from-amber-600',
    to: 'to-amber-400',
    text: 'text-amber-600',
    bg: 'bg-amber-50',
  },
  commercial: {
    from: 'from-violet-600',
    to: 'to-violet-400',
    text: 'text-violet-600',
    bg: 'bg-violet-50',
  },
};

export default function CategorySection({ categories, getIcon, counts }: Props) {
  const { t } = useTranslation();
  const isRu = i18n.language?.startsWith('ru');

  return (
    <section className="container mx-auto px-3 sm:px-4 py-10 md:py-16">
      {/* Header */}
      <div className="text-center mb-7 md:mb-12">
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-gray-900">
          {t('categories.title')}
        </h2>
        <p className="mt-2 sm:mt-3 text-sm md:text-base text-gray-500 max-w-xl mx-auto">
          {t(
            'categories.subtitle',
            'Выберите подходящую категорию недвижимости',
          )}
        </p>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
        {/* Empty categories stay hidden until they get a listing ("0 объявлений" looks dead). */}
        {categories.filter((c) => counts?.[c.value] !== 0).map((category) => {
          const colors = CATEGORY_COLORS[category.id] ?? {
            from: 'from-blue-600',
            to: 'to-blue-400',
            text: 'text-blue-600',
            bg: 'bg-blue-50',
          };

          const count = counts?.[category.value];
          const countLabel =
            count === undefined
              ? null
              : isRu
                ? `${count} ${pluralizeRu(count, ['объект', 'объекта', 'объектов'])}`
                : `${count} ${t('categories.objectsWord', { defaultValue: 'объект' })}`;

          return (
            <Link
              key={category.id}
              to={
                SEO_CATEGORIES.find((c) => c.value === category.value)
                  ? hubPath(SEO_CATEGORIES.find((c) => c.value === category.value)!.slug)
                  : `/listings?category=${category.value}`
              }
              className="
                group relative flex flex-col
                rounded-2xl sm:rounded-3xl overflow-hidden
                bg-white border border-gray-100
                shadow-sm hover:shadow-xl hover:-translate-y-1
                active:scale-[0.97]
                transition-all duration-300 ease-out
              "
            >
              {/* ── IMAGE ── */}
              <div className="relative h-32 xs:h-36 sm:h-48 md:h-56 overflow-hidden flex-shrink-0">
                <img
                  src={CATEGORY_IMAGES[category.id]}
                  alt={category.id}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

                {/* Colored top accent line */}
                <div
                  className={`absolute top-0 inset-x-0 h-1 bg-gradient-to-r ${colors.from} ${colors.to}`}
                />

                {/* Object-count badge — top right of image */}
                {countLabel && (
                  <div className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 rounded-full bg-white/95 backdrop-blur-md px-2.5 py-1 text-[10px] sm:text-[11px] font-bold text-gray-800 shadow-md">
                    {countLabel}
                  </div>
                )}

                {/* Icon pill — bottom left of image */}
                <div
                  className={`
                    absolute bottom-2.5 left-2.5 sm:bottom-3 sm:left-3
                    w-9 h-9 sm:w-11 sm:h-11
                    rounded-xl sm:rounded-2xl
                    bg-white/95 backdrop-blur-md
                    border border-white/60
                    flex items-center justify-center
                    ${colors.text}
                    shadow-lg
                    transition-transform duration-300 group-hover:scale-110
                  `}
                >
                  {getIcon(category.icon, { size: 16 })}
                </div>
              </div>

              {/* ── CONTENT ── */}
              <div className="flex items-center justify-between gap-2 p-3 sm:p-4">
                <div className="min-w-0">
                  <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate group-hover:text-blue-600 transition-colors duration-200">
                    {t(`categories.${category.id}`)}
                  </h3>
                  <p className="text-[11px] sm:text-xs text-gray-400 mt-0.5">
                    {t('categories.explore', 'Смотреть все')}
                  </p>
                </div>

                {/* Arrow circle */}
                <div
                  className={`
                    shrink-0
                    w-7 h-7 sm:w-9 sm:h-9
                    rounded-full
                    ${colors.bg} border border-gray-100
                    flex items-center justify-center
                    ${colors.text}
                    transition-all duration-300
                    group-hover:scale-110
                  `}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 14 14"
                    fill="none"
                    className="sm:w-[14px] sm:h-[14px] transition-transform duration-300 group-hover:translate-x-0.5"
                  >
                    <path
                      d="M2 7h10M7 2l5 5-5 5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
